import type { AchievementNamesDto, GameDto, GameRarityDto, UnlockDto } from "@steam/contracts";

import { formatNumber } from "../i18n/format-number";
import type { Translate } from "../i18n/i18n";
import { gamesCounted, type LibraryView } from "./library";

/**
 * What Steam publishes about each game, keyed by appId. A game absent has not
 * been asked about yet; a game present with an empty list is one Steam
 * publishes nothing about. Neither can be ranked, and the difference belongs to
 * whoever is doing the asking.
 */
export type RarityByAppId = Readonly<Record<number, GameRarityDto>>;

/**
 * One line of the ranking, decided here and drawn as it stands. Named for what
 * it is rather than for the row that draws it, as an UnlockMonth is.
 */
export type RarestUnlock = {
  readonly appId: number;
  /** Which game this came from — across a library, that is what places it. */
  readonly gameName: string;
  /**
   * Unique within its game, and only within it. No display name: naming an
   * achievement needs the schema, which is the heavy payload ADR-0005 keeps
   * out of the library's path and which only the games shown here will pay.
   */
  readonly apiName: string;
  readonly rarity: number;
  /** The figure as the row writes it — `0.4%`, never `0%` of something held. */
  readonly rarityLabel: string;
};

export type RarestUnlocks = {
  /** Rarest first, and never longer than the ties at the cut make it. */
  readonly rows: readonly RarestUnlock[];
  /**
   * What was ranked and what it was ranked across — `rarest 10 across 214
   * games counted`. A library holds games nobody publishes figures for, and a
   * ranking that did not say so would read as the whole library's answer.
   */
  readonly countedLabel: string;
};

/**
 * How many rows the tab shows before ties extend it. Ten is a list a reader
 * takes in at once, and the eleventh rarest thing in a library is not what the
 * tab was opened for.
 */
const ROWS = 10;

/** One candidate row, with the date the cut and the ties are decided on. */
type Candidate = RarestUnlock & { readonly at: number | null };

/**
 * At most one decimal, and the trailing zero dropped — `12%`, `0.4%`, as every
 * other rate on this screen is written. A figure that rounds to nothing is
 * written as under a tenth rather than as zero: the player is holding the
 * achievement, so "0%" of them would be a plain untruth.
 */
const rarityLabelOf = (rarity: number, t: Translate): string => {
  const rounded = Math.round(rarity * 10) / 10;
  return rounded === 0 ? `<${formatNumber(t, 0.1, 1)}%` : `${formatNumber(t, rounded, 1)}%`;
};

/**
 * An unlock Steam will not date has no day to be placed by, and sorts as older
 * than any day there is — which puts it behind its dated equals rather than at
 * either end of a scale Steam refused to place it on. Epoch seconds are never
 * negative, so nothing real can reach this.
 */
const NEVER_DATED_LAST = -1;

const whenOf = (candidate: Candidate): number => candidate.at ?? NEVER_DATED_LAST;

/**
 * Rarest first; among equals the newest unlock first, since Steam's rounding
 * makes real ties and the newest trophy is the one worth leading with.
 *
 * The last two comparisons decide nothing a reader would notice and everything
 * a rebuild would: the same inputs must give the same order, and two unlocks
 * can be equal on both a figure and a day.
 */
const byRarestThenNewest = (a: Candidate, b: Candidate): number => {
  if (a.rarity !== b.rarity) return a.rarity - b.rarity;

  const [when, otherWhen] = [whenOf(a), whenOf(b)];
  // Reversed, because the newer unlock leads among equals.
  if (when !== otherWhen) return otherWhen - when;

  if (a.appId !== b.appId) return a.appId - b.appId;
  return a.apiName < b.apiName ? -1 : 1;
};

/**
 * The unlocks of one game that Steam publishes a figure for. An unlock with no
 * published figure is dropped rather than given one: a Rarity we do not hold is
 * not a Rarity of zero, which would rank it the rarest thing in the library.
 *
 * Only unlocks are looked at at all, so a locked achievement — however rare —
 * cannot reach the ranking. This tab is what the player has actually earned.
 */
const candidatesIn = (
  game: GameDto,
  unlocks: readonly UnlockDto[],
  published: GameRarityDto,
  t: Translate,
): readonly Candidate[] => {
  const rarityOf = new Map(published.map((one) => [one.apiName, one.rarity]));

  return unlocks.flatMap((unlock) => {
    const rarity = rarityOf.get(unlock.apiName);
    if (rarity === undefined) return [];
    return [
      {
        appId: game.appId,
        gameName: game.name,
        apiName: unlock.apiName,
        rarity,
        rarityLabel: rarityLabelOf(rarity, t),
        at: unlock.at,
      },
    ];
  });
};

/**
 * The ten rarest, and everything the tenth is tied with. Steam rounds to one
 * decimal, so the tenth and the eleventh can be published at exactly the same
 * figure, and cutting between two identical values is the one place this
 * ranking can mislead without anyone noticing.
 *
 * The tie is read off the label rather than off the figure behind it: what a
 * reader can see is what they would notice being cut. The two agree on
 * everything Steam has ever been measured sending, and where they would not,
 * the label is the honest one.
 */
const topWithItsTies = (ranked: readonly Candidate[]): readonly Candidate[] => {
  const last = ranked[ROWS - 1];
  if (!last) return ranked;
  return ranked.filter((one, index) => index < ROWS || one.rarityLabel === last.rarityLabel);
};

const labelFor = (rows: number, counted: number, t: Translate): string =>
  rows === 0
    ? t("rarest.nothingToRank", { counted: gamesCounted(counted, t) })
    : t("rarest.ranked", { count: rows, counted: gamesCounted(counted, t) });

/**
 * The rarest achievements this player has actually unlocked, rarest first,
 * ready to draw: every decision the tab makes is made here, so the components
 * that draw it decide nothing.
 *
 * Reads the same LibraryView the rows and the summary are built from, plus what
 * came back from the rarity route — which knows no player, and so cannot be
 * asked this question on its own.
 *
 * Pure: no clock and no network. It is rebuilt on every wave of answers, and a
 * ranking that moved under the reader between two identical loads would be a
 * different answer to the same question.
 */
export const buildRarestUnlocks = (
  view: LibraryView,
  rarity: RarityByAppId,
  t: Translate,
): RarestUnlocks => {
  const ranked: Candidate[] = [];
  let counted = 0;

  for (const game of view.games) {
    const tally = view.tallies[game.appId];
    const published = rarity[game.appId];
    // A game still being counted, or one Steam publishes nothing about, is not
    // a game this ranking has ranked across, and says so by not counting it.
    if (!tally || !published || published.length === 0) continue;

    counted += 1;
    ranked.push(...candidatesIn(game, tally.unlocks, published, t));
  }

  const rows = topWithItsTies([...ranked].sort(byRarestThenNewest)).map(
    ({ at: _at, ...row }) => row,
  );

  return { rows, countedLabel: labelFor(rows.length, counted, t) };
};

/**
 * How each game names its own achievements, keyed by appId. A game absent has
 * not been asked about — which is every game until a ranking exists, since what
 * to ask about is read off the rows.
 */
export type NamesByAppId = Readonly<Record<number, AchievementNamesDto>>;

/**
 * A ranked row once its game has named it. The name and the icon arrive after
 * the ranking, from a second fetch the ranking itself decides the shape of, so
 * they are held apart from what a row was ranked on.
 */
export type NamedUnlock = RarestUnlock & {
  /** What the game calls it, or the apiName until the game has said. */
  readonly displayName: string;
  /** The unlocked icon, or null where the game has named nothing for it. */
  readonly icon: string | null;
  /**
   * Whether this row's game has yet to answer — the row has nothing to show,
   * as against having been shown there is nothing.
   *
   * The two cannot share a drawing. An apiName is a key, and a row wearing one
   * does not look like a row that is waiting: it looks like a game that names
   * its achievements ACH_ASCEND_10. So a row still waiting is drawn as waiting,
   * and only a row whose game has answered settles for the apiName (#57).
   */
  readonly pending: boolean;
};

/**
 * The games the shown rows come from, each once, in the order the rows show
 * them — three to six of them for a ten-row ranking.
 *
 * This is what bounds phase two. The schema is the 253 KB payload ADR-0005 took
 * out of the library's path, and it can only be asked for once the ranking
 * exists: which games matter is a property of the answer, not of the library.
 */
export const gamesShownIn = (rows: readonly RarestUnlock[]): readonly number[] => [
  ...new Set(rows.map((row) => row.appId)),
];

/**
 * The rows as a reader should see them: named, iconed, and otherwise untouched.
 *
 * Nothing is dropped and nothing moves. A row earned its place on a figure
 * Steam published and a day the player unlocked it, and an answer about its
 * name cannot take that back — so an achievement its game says nothing about
 * keeps the apiName it was ranked under. A poor name is not a blank row.
 *
 * `pending` is the games still to answer, and is not derivable from `names`: a
 * game absent from there is one that was never asked and one that could not be
 * named alike, and those two rows are drawn differently.
 */
export const nameUnlocks = (
  rows: readonly RarestUnlock[],
  names: NamesByAppId,
  pending: ReadonlySet<number>,
): readonly NamedUnlock[] =>
  rows.map((row) => {
    const named = names[row.appId]?.find((achievement) => achievement.apiName === row.apiName);
    return {
      ...row,
      // `||` rather than `??`, and deliberately: a name Steam sent empty draws
      // an empty row, which is worse than the key the row was ranked under.
      displayName: named?.displayName || row.apiName,
      icon: named?.icon ?? null,
      pending: pending.has(row.appId),
    };
  });
